import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react'
import api from '../../services/api'
import { formatDateBR } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

// Busca solicitações de feedback
const fetchFeedbackRequests = async (filters) => {
  const response = await api.get('/api/feedback-requests', { params: filters })
  return response.data.data
}

// Status badge
function StatusBadge({ status }) {
  const statusConfig = {
    enviado: { label: 'Enviado', color: 'bg-blue-100 text-blue-700' },
    reenviado: { label: 'Reenviado', color: 'bg-yellow-100 text-yellow-700' },
    respondido: { label: 'Respondido', color: 'bg-green-100 text-green-700' },
    sem_resposta: { label: 'Sem resposta', color: 'bg-gray-100 text-gray-500' },
  }
  const config = statusConfig[status] || statusConfig.enviado
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

export default function FeedbackList() {
  const [filters, setFilters] = useState({ page: 1, limit: 20 })

  const { data, isLoading } = useQuery({
    queryKey: ['feedbackRequests', filters],
    queryFn: () => fetchFeedbackRequests(filters),
  })

  const handleStatusChange = (status) => {
    setFilters({ ...filters, status: status || undefined, page: 1 })
  }

  const pagination = data?.pagination

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-title">Feedback</h1>
        <p className="page-subtitle">Acompanhe as solicitações de feedback enviadas aos destinatários.</p>
      </div>

      {/* Filtros */}
      <Card padding="sm">
        <select
          value={filters.status || ''}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="border-0 focus:ring-0 text-gray-600"
        >
          <option value="">Todos os status</option>
          <option value="enviado">Enviado</option>
          <option value="reenviado">Reenviado</option>
          <option value="respondido">Respondido</option>
          <option value="sem_resposta">Sem resposta</option>
        </select>
      </Card>

      {/* Lista */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : data?.feedbackRequests?.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma solicitação de feedback encontrada</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {data?.feedbackRequests?.map((item) => (
              <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-gray-900">{item.recipientName}</p>
                    <p className="text-sm text-gray-500">
                      {item.trackingCode} · Agendada: {formatDateBR(item.scheduledDate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {(item.score !== null && item.score !== undefined) && (
                      <span className="text-sm font-semibold text-gray-700">Nota: {item.score}</span>
                    )}
                    <StatusBadge status={item.status} />
                  </div>
                </div>
                {item.responseText && (
                  <p className="text-sm text-gray-600 mt-2 italic">"{item.responseText}"</p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {item.sentAt && `Enviado em ${new Date(item.sentAt).toLocaleString('pt-BR')}`}
                  {item.resentAt && ` · Reenviado em ${new Date(item.resentAt).toLocaleString('pt-BR')}`}
                  {item.respondedAt && ` · Respondido em ${new Date(item.respondedAt).toLocaleString('pt-BR')}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Página {pagination.page} de {pagination.totalPages} ({pagination.total} no total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={!pagination.hasPrev}
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!pagination.hasNext}
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
